import { useState } from 'react';
import {
  AlertCircle, FileText, CheckCircle2, FileDown, Info, RefreshCw,
} from 'lucide-react';
import { signInWithGoogle } from '../lib/auth';
import { driveImageUrl } from '../lib/formatters';
import { APP_LOGO_DRIVE_ID, APP_THEMES } from '../lib/constants';
import { useReplicaSlug } from '../hooks/useReplica';
import { getReplicaConfig } from '../lib/replicas.config';

// =============================================================================
// LoginView — Google OAuth + branding (layout 2 colunas estilo inventario)
// =============================================================================
// Versao 'canonica' do login. Tema adapta a cor/logo/label da replica
// corrente via APP_THEMES e replicas.config. Variante experimental com
// Particle Text Effect existe em LoginView3.jsx (backup).
// =============================================================================

// Helper: dado um slug de replica (ou null em principal), retorna a paleta
// "verde" usada pelo LoginView (cores + logo + label). Em principal volta
// pro teal do inventario.
const PRINCIPAL_THEME = {
  green:     '#23c483',
  greenSoft: 'rgba(35, 196, 131, 0.12)',
  greenDeep: '#19a06a',
};
function resolveLoginTheme(slug) {
  const cfg = getReplicaConfig(slug);
  if (!cfg) {
    return {
      ...PRINCIPAL_THEME,
      logoId: APP_LOGO_DRIVE_ID,
      label: 'FATURAMENTO',
    };
  }
  const t = APP_THEMES[cfg.temaCor];
  const logoIsValid = cfg.logoId && cfg.logoId !== 'ei johnny' && cfg.logoId.trim() !== '';
  return {
    green:     t ? t.primary     : PRINCIPAL_THEME.green,
    greenSoft: t ? t.primarySoft : PRINCIPAL_THEME.greenSoft,
    greenDeep: t ? t.primary2    : PRINCIPAL_THEME.greenDeep,
    logoId:    logoIsValid ? cfg.logoId : APP_LOGO_DRIVE_ID,
    label:     (cfg.label || 'FATURAMENTO').toUpperCase(),
  };
}

// Logo Google oficial (multi-color G) como SVG inline — mesmo padrao usado
// na relatorios/fluxo de caixa. Fica num <span> com tamanho fixo dentro
// do botao.
const GoogleLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

export function LoginView() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  // Em /bhaz, /llcs, etc — adapta a tela de login pra cor e logo da
  // empresa daquela replica. Em / (principal) usa o teal do inventario.
  const slug = useReplicaSlug();
  const theme = resolveLoginTheme(slug);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      // Redireciona pro Google. Quando voltar, useSession captura a sessao
      // e o App.jsx troca de tela. Se nao tiver app_access pra faturamento,
      // AuthedApp/AccessDenied lida com isso depois (controle do painel admin).
    } catch (err) {
      setError(err?.message || 'Erro ao iniciar login com Google.');
      setLoading(false);
      triggerShake();
    }
  };

  const features = [
    { icon: FileText,     label: 'Solicitações'    },
    { icon: CheckCircle2, label: 'Acompanhamento'  },
    { icon: FileDown,     label: 'PDF Espelho'     },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, position: 'relative', overflow: 'hidden',
      background: 'var(--lv-bg-base)', color: 'var(--lv-text-primary)',
      fontFamily: "'Geist', system-ui, sans-serif",
      // Sobrescreve as CSS vars de accent — em replica usa a cor da
      // empresa (APP_THEMES[temaCor]), em principal mantem o teal default.
      '--lv-green':      theme.green,
      '--lv-green-soft': theme.greenSoft,
      '--lv-green-deep': theme.greenDeep,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600;700&display=swap');
        :root {
          --lv-bg-base:#0a0e1a;
          --lv-bg-elev-1:#131825;
          --lv-bg-elev-2:#1a2030;
          --lv-bg-elev-3:#232a3d;
          --lv-border-soft:rgba(255,255,255,0.06);
          --lv-border-mid:rgba(255,255,255,0.10);
          --lv-border-strong:rgba(255,255,255,0.18);
          --lv-text-primary:#e8eaf0;
          --lv-text-secondary:#9ba3b4;
          --lv-text-muted:#6b7184;
          --lv-green:#23c483;
          --lv-green-soft:rgba(35,196,131,0.12);
          --lv-green-deep:#19a06a;
          --lv-blue:#5b9bff;
          --lv-blue-soft:rgba(91,155,255,0.12);
          --lv-red:#ef5a5a;
          --lv-red-soft:rgba(239,90,90,0.12);
        }
        .lv-grain{
          position:absolute; inset:0; pointer-events:none;
          background-image:
            radial-gradient(at 20% 0%,   rgba(35,196,131,0.06) 0, transparent 50%),
            radial-gradient(at 80% 100%, rgba(91,155,255,0.04) 0, transparent 50%);
        }
        .lv-display{ font-family:'Geist',system-ui,sans-serif; letter-spacing:-0.035em; font-weight:700; }
        .lv-mono   { font-family:'Geist Mono',monospace; letter-spacing:-0.01em; }
        /* Botao Google: fundo branco com texto escuro — segue o style guide
           oficial do Google Sign-In. Distinto do botao verde primario. */
        .lv-google-btn{
          width:100%; display:inline-flex; align-items:center; justify-content:center; gap:10px;
          padding:12px 18px; border-radius:12px;
          border:1px solid var(--lv-border-mid);
          cursor:pointer; font-weight:600; font-size:14px;
          font-family:'Geist',system-ui,sans-serif;
          background:#fff; color:#1f2937;
          transition:opacity .15s, transform .1s, box-shadow .15s;
        }
        .lv-google-btn:hover:not(:disabled){
          box-shadow:0 4px 12px rgba(0,0,0,0.25);
        }
        .lv-google-btn:active:not(:disabled){ transform:scale(0.98); }
        .lv-google-btn:disabled{ opacity:0.6; cursor:not-allowed; }
        .lv-card{ background:var(--lv-bg-elev-1); border:1px solid var(--lv-border-soft); border-radius:16px; }
        .lv-feature{ background:var(--lv-bg-elev-1); border:1px solid var(--lv-border-soft); border-radius:16px; padding:16px; }
        @keyframes lvShake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
        .lv-shake{ animation:lvShake .4s ease; }
        @keyframes lvSpin{ from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .lv-spin{ animation:lvSpin .7s linear infinite; }
        @keyframes lvFadeIn{ from{opacity:0; transform:translateY(8px)} to{opacity:1; transform:none} }
        .lv-fade{ animation:lvFadeIn .35s ease; }
        @media (max-width: 1024px) {
          .lv-grid    { grid-template-columns:1fr !important; }
          .lv-title   { font-size:42px !important; }
          .lv-features{ grid-template-columns:repeat(3,1fr) !important; }
        }
      `}</style>

      <div className="lv-grain" />

      <div className={`lv-fade${shake ? ' lv-shake' : ''}`}
        style={{ width: '100%', maxWidth: 1080, position: 'relative', zIndex: 1 }}>
        <div className="lv-grid" style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 48, alignItems: 'center',
        }}>

          {/* ── HERO (esquerda) ───────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* No modo principal (/) a logo ETUS e renderizada via CSS mask
                  pra ficar SOLIDA na cor verde (efeito estilizado). Em modo
                  replica (/bhaz, /supersonic, etc), cada empresa tem sua
                  propria marca colorida — entao usamos uma <img> simples
                  pra preservar as cores originais da logo. */}
              {slug ? (
                <img
                  src={driveImageUrl(theme.logoId)}
                  alt={theme.label || 'logo'}
                  style={{
                    height: 48,
                    width: 'auto',
                    maxWidth: 160,
                    objectFit: 'contain',
                    filter: `drop-shadow(0 8px 32px ${theme.greenSoft})`,
                  }}
                />
              ) : (
                <div
                  role="img"
                  aria-label="ETUS"
                  style={{
                    height: 48, width: 120,
                    background: 'var(--lv-green)',
                    WebkitMask: `url(${driveImageUrl(theme.logoId)}) center / contain no-repeat`,
                    mask: `url(${driveImageUrl(theme.logoId)}) center / contain no-repeat`,
                    filter: `drop-shadow(0 8px 32px ${theme.greenSoft})`,
                  }}
                />
              )}
              <div>
                <div style={{
                  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.2em',
                  color: 'var(--lv-text-muted)',
                }}>
                  ETUS Group
                </div>
                <div className="lv-mono" style={{
                  fontSize: 13, fontWeight: 500, color: 'var(--lv-green)',
                }}>
                  {theme.label}
                </div>
              </div>
            </div>

            <div>
              <div className="lv-mono" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 9999,
                fontSize: 10, fontWeight: 500,
                textTransform: 'uppercase', letterSpacing: '0.15em',
                background: 'var(--lv-green-soft)', color: 'var(--lv-green)',
                marginBottom: 20,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--lv-green)' }} />
                v1.0 — Beta
              </div>
              <h1 className="lv-display lv-title" style={{
                margin: 0, fontSize: 60, lineHeight: 1.0,
                marginBottom: 16, color: 'var(--lv-text-primary)',
              }}>
                Sistema de<br />
                <span style={{ color: 'var(--lv-green)' }}>Solicitação</span><br />
                de Notas Fiscais
              </h1>
              <p style={{
                margin: 0, fontSize: 16, maxWidth: 480, lineHeight: 1.6,
                color: 'var(--lv-text-secondary)',
              }}>
                Solicite, acompanhe e emita todas as notas fiscais de serviço em
                uma plataforma única — com transparência, controle e padronização.
              </p>
            </div>

            <div className="lv-features" style={{
              display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, paddingTop: 8,
            }}>
              {features.map(({ icon: Icon, label }) => (
                <div key={label} className="lv-feature">
                  <Icon size={18} style={{ color: 'var(--lv-green)' }} strokeWidth={2.2} />
                  <div style={{ fontSize: 12, marginTop: 8, color: 'var(--lv-text-secondary)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── CARD DE LOGIN (direita) — so Google ────────────────────────── */}
          <div className="lv-card" style={{ padding: 32 }}>
            <h2 className="lv-display" style={{
              margin: 0, fontSize: 30, marginBottom: 4, color: 'var(--lv-text-primary)',
            }}>
              Entrar
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--lv-text-secondary)' }}>
              Acesse com sua conta corporativa Google
            </p>

            <button
              type="button"
              className="lv-google-btn"
              onClick={handleGoogle}
              disabled={loading}
            >
              {loading
                ? <><RefreshCw size={16} className="lv-spin" />Redirecionando…</>
                : <><GoogleLogo size={18} />Continuar com Google</>
              }
            </button>

            {error && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 12,
                fontSize: 12.5, color: 'var(--lv-red)',
                background: 'var(--lv-red-soft)',
                border: '1px solid rgba(239,90,90,0.30)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div style={{
              marginTop: 24, padding: 14, borderRadius: 12,
              display: 'flex', gap: 12,
              background: 'var(--lv-blue-soft)', border: '1px solid var(--lv-border-soft)',
            }}>
              <Info size={18} style={{ flexShrink: 0, marginTop: 2, color: 'var(--lv-blue)' }} />
              <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--lv-text-secondary)' }}>
                Acesso restrito a e-mails <span className="lv-mono" style={{
                  background: 'var(--lv-bg-elev-3)', color: 'var(--lv-text-primary)',
                  padding: '1px 6px', borderRadius: 4,
                }}>@etus.com.br</span>. Sua permissão é definida pelo administrador no primeiro acesso.
              </div>
            </div>
          </div>
        </div>

        <p style={{
          textAlign: 'center', fontSize: 11.5, color: 'var(--lv-text-muted)', marginTop: 32,
        }}>
          © {new Date().getFullYear()} ETUS Group · Solicitação de Notas Fiscais
        </p>
      </div>
    </div>
  );
}
